import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth-service/auth.service';
import { SharedDataService } from '../shared-service/shared-data.service';
import { SocketService } from '../socket-service/socket-service.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class AutoLogoutService {
  // HIPAA / NEMT TMS Compliance Standard: 30 minutes inactivity timeout
  private readonly INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
  // Production Check Interval: Every 10 seconds while session is active
  private readonly CHECK_INTERVAL_MS = 10 * 1000;
  // LocalStorage Write Throttle: At most once every 2 seconds
  private readonly ACTIVITY_THROTTLE_MS = 2000;

  private readonly STORAGE_KEY_LAST_ACTIVITY = 'last_activity_time';
  private readonly STORAGE_KEY_LOGOUT_EVENT = 'logout_event';
  private readonly STORAGE_KEY_LOGIN_EVENT = 'login_event';

  private checkIntervalTimer: any = null;
  private lastActivityWriteTime = 0;
  private isInitialized = false;
  private isMonitoring = false;
  private user: any = null;

  // Track bound activity listeners for clean removal
  private activityListeners: Array<{ target: EventTarget; eventName: string; handler: EventListenerOrEventListenerObject }> = [];

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private authService: AuthService,
    private sharedDataService: SharedDataService,
    private socketService: SocketService,
    private toastr: ToastrService
  ) { }

  /**
   * Check if Auto Logout is enabled at the company/user level.
   */
  public isAutoLogoutEnabled(targetUser: any = this.user): boolean {
    if (!targetUser) return false;
    if (targetUser.hasOwnProperty('isAutoLogout') && targetUser.isAutoLogout === true) {
      return true;
    }
    if (targetUser.company && targetUser.company.hasOwnProperty('isAutoLogout') && targetUser.company.isAutoLogout === true) {
      return true;
    }
    return false;
  }

  /**
   * Initialize auto-logout service and cross-tab sync listeners.
   */
  public init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Listen for cross-tab storage events (login & logout) across all open tabs
    this.setupCrossTabStorageSync();

    // Subscribe to user state changes
    this.sharedDataService.getUser().subscribe(user => {
      this.user = user;
      if (user && this.authService.getToken('token') && this.isAutoLogoutEnabled(user)) {
        // Enforce initial startup check
        const storedLastActive = this.getLastActivityTime();
        const elapsed = Date.now() - storedLastActive;
        if (storedLastActive > 0 && elapsed >= this.INACTIVITY_LIMIT_MS) {
          this.executeLogout(true);
          return;
        }
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });
  }

  /**
   * Cross-tab sync:
   * 1. When another tab logs in -> auto reload/sync this tab
   * 2. When another tab logs out -> auto log out this tab
   */
  private setupCrossTabStorageSync() {
    window.addEventListener('storage', (event: StorageEvent) => {
      // 1. Cross-tab Login Sync
      if (event.key === this.STORAGE_KEY_LOGIN_EVENT && event.newValue) {
        const currentToken = this.authService.getToken('token');
        if (!currentToken || !this.user || window.location.pathname.includes('login')) {
          this.ngZone.run(() => {
            window.location.reload();
          });
        }
      }

      // 2. Cross-tab Logout Sync
      if (event.key === this.STORAGE_KEY_LOGOUT_EVENT && event.newValue) {
        const currentToken = this.authService.getToken('token');
        if (currentToken || this.user) {
          this.ngZone.run(() => {
            this.executeLogout(false);
          });
        }
      }
    });
  }

  /**
   * Start listening to user interaction and running inactivity check timer.
   * ONLY runs when user is logged in AND isAutoLogout is enabled.
   */
  public startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;

    this.updateActivity();

    // Run DOM event listeners outside Angular zone for high performance (60fps)
    this.ngZone.runOutsideAngular(() => {
      this.attachActivityListeners();
      this.startCheckTimer();
    });
  }

  /**
   * Stop all activity listeners and timer when logged out or when isAutoLogout is false.
   */
  public stopMonitoring() {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;
    this.removeActivityListeners();
    this.stopCheckTimer();
  }

  private attachActivityListeners() {
    this.removeActivityListeners();

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'touchmove',
      'scroll',
      'click',
      'wheel'
    ];

    const onUserActivity = () => {
      this.handleUserActivity();
    };

    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, onUserActivity, { passive: true });
      this.activityListeners.push({ target: window, eventName, handler: onUserActivity });
    });

    // Handle tab visibility / focus changes (e.g. user returns to tab after absence)
    const onVisibilityOrFocusChange = () => {
      if (document.visibilityState === 'visible' || document.hasFocus()) {
        const isTimedOut = this.checkInactivityTimeout();
        if (!isTimedOut) {
          this.handleUserActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityOrFocusChange);
    this.activityListeners.push({ target: document, eventName: 'visibilitychange', handler: onVisibilityOrFocusChange });

    window.addEventListener('focus', onVisibilityOrFocusChange);
    this.activityListeners.push({ target: window, eventName: 'focus', handler: onVisibilityOrFocusChange });

    // Handle browser / tab close to save exact exit timestamp
    const onBeforeUnload = () => {
      if (this.authService.getToken('token') && this.isAutoLogoutEnabled()) {
        this.updateActivity();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    this.activityListeners.push({ target: window, eventName: 'beforeunload', handler: onBeforeUnload });
  }

  private removeActivityListeners() {
    this.activityListeners.forEach(({ target, eventName, handler }) => {
      target.removeEventListener(eventName, handler);
    });
    this.activityListeners = [];
  }

  private handleUserActivity() {
    if (!this.isMonitoring) return;
    const now = Date.now();
    // Throttle writes to localStorage to prevent disk saturation
    if (now - this.lastActivityWriteTime >= this.ACTIVITY_THROTTLE_MS) {
      this.updateActivity();
    }
  }

  public updateActivity() {
    const now = Date.now();
    this.lastActivityWriteTime = now;
    try {
      localStorage.setItem(this.STORAGE_KEY_LAST_ACTIVITY, now.toString());
    } catch (e) { }
  }

  public getLastActivityTime(): number {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_LAST_ACTIVITY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return parsed;
        }
      }
    } catch (e) { }
    return this.lastActivityWriteTime || Date.now();
  }

  private startCheckTimer() {
    this.stopCheckTimer();
    this.checkIntervalTimer = setInterval(() => {
      this.checkInactivityTimeout();
    }, this.CHECK_INTERVAL_MS);
  }

  private stopCheckTimer() {
    if (this.checkIntervalTimer) {
      clearInterval(this.checkIntervalTimer);
      this.checkIntervalTimer = null;
    }
  }

  /**
   * Verify if inactivity limit (30 minutes) has elapsed across all tabs.
   * Returns true if timeout occurred.
   */
  private checkInactivityTimeout(): boolean {
    if (!this.authService.getToken('token') || !this.isMonitoring || !this.isAutoLogoutEnabled()) {
      return false;
    }

    const lastActivity = this.getLastActivityTime();
    const now = Date.now();
    const elapsed = now - lastActivity;

    if (elapsed >= this.INACTIVITY_LIMIT_MS) {
      this.ngZone.run(() => {
        this.executeLogout(true);
      });
      return true;
    }
    return false;
  }

  /**
   * Perform logout: clear tokens, disconnect socket, clear shared state, and redirect.
   */
  public executeLogout(isInitiator: boolean = true) {
    const token = this.authService.getToken('token');
    if (!token && !this.user) {
      return;
    }

    this.stopMonitoring();

    if (isInitiator) {
      try {
        // Broadcast logout event to all other tabs via localStorage storage event
        localStorage.setItem(this.STORAGE_KEY_LOGOUT_EVENT, Date.now().toString());
      } catch (e) { }
    }

    // Clean up local activity keys
    try {
      localStorage.removeItem(this.STORAGE_KEY_LAST_ACTIVITY);
    } catch (e) { }

    // Disconnect socket & remove credentials
    try {
      this.socketService.disconnectSocket();
    } catch (e) { }

    this.authService.removeToken();
    this.authService.removeAuthToken();
    this.sharedDataService.saveUser(null);

    const logoutURL = this.user && this.user.logoutURL ? this.user.logoutURL : null;

    if (isInitiator) {
      try {
        this.toastr.warning('You have been logged out due to 30 minutes of inactivity.', 'Session Expired');
      } catch (e) { }
    }

    if (logoutURL) {
      window.open(logoutURL, '_self');
    } else {
      this.router.navigateByUrl('login');
    }
  }
}
